import { ArrowLeftOutlined } from "@ant-design/icons";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { TAB_HELP } from "@wms/domain";
import type { EntityMeta, Workflow } from "@wms/domain";
import { Alert, Button, Card, Segmented, Space, Tabs, Tag, Typography } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, describeError } from "../api/client";
import { useAuthStore } from "../auth/store";
import { ActionPanel } from "../components/ActionPanel";
import { AttachmentsTab } from "../components/AttachmentsTab";
import { EntityForm } from "../components/EntityForm";
import type { Row } from "../components/EntityForm";
import { EntityTable } from "../components/EntityTable";
import { HelpHint } from "../components/HelpHint";
import { LoadProblem } from "../components/LoadProblem";
import { OperationTag, documentWord, useCardOperation } from "../components/OperationTag";
import { refreshChildLists } from "../components/recordQueries";
import { StepBlock, StepFooter, StepStrip } from "../components/StepwiseCard";
import { StockPickerButton } from "../components/StockPicker";
import type { CardView } from "../components/stepwise";
import { buildSteps, defaultStep, formParts, neighbours, parseView, readStoredView, stepOfPart, stepOfTab, storeView } from "../components/stepwise";

/**
 * A tab name with its «(?)» (UI-19): the hint opens without switching the tab. UX3010-03: the tab the workflow
 * recommends next («Товары» of a fresh receipt) carries a green frame and the words «Следующий шаг» — told apart from
 * the current tab (blue ink) in both themes and read out by a screen reader with the tab name.
 */
function tabLabel(label: string, help?: string | null, next?: boolean) {
  return (
    <span className={next ? "wms-tab-next" : undefined} data-next-step={next || undefined} data-testid={next ? "tab-next-step" : undefined}>
      {label}
      {help && <HelpHint subject={label} text={help} size={13} />}
      {next && <span className="wms-next-badge">Следующий шаг</span>}
    </span>
  );
}

/**
 * The page of a record: title, the meta-driven form (EntityForm) and the tabs of its children, files and history.
 * The same form is reused by the «+» drawer inside reference lists, so this page owns only what a page has —
 * the route, the title and the tabs.
 *
 * ТЗ «Понятные шаги» (30.10.2026): with the option «Понятные шаги: карточка по шагам» the same page can show the
 * record by steps — the strip of stages, one step at a time, one main action — and switch back to the full card.
 * Both views share one form element and one set of panes (the form, the child lists, files, history): the panes
 * stay mounted at the same place of the tree, so switching the view or the step loses no typed value, filter or
 * selection (UX1-011, UX1-017). With the option off the page is the card of HEAD.
 */
/** CW31: «Обращение на сверку», not «Обращение на сверку:» — a record without a number or a name has no tail. */
function cardTitle(label: string, name: unknown): string {
  const tail = name === null || name === undefined ? "" : String(name).trim();
  return tail ? `${label}: ${tail}` : label;
}

export function EntityFormPage() {
  const { entity = "", id = "new" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const isNew = id === "new";
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const can = useAuthStore((s) => s.can);
  const option = useAuthStore((s) => s.option);
  const savePreferences = useAuthStore((s) => s.savePreferences);
  const preferredView = useAuthStore((s) => s.me?.preferences?.card_view as string | undefined);
  const [dirty, setDirty] = useState(false);
  const [activeTab, setActiveTab] = useState("main");

  const meta = useQuery({ queryKey: ["meta", entity], queryFn: () => api.get<EntityMeta>(`/${entity}/meta`) });
  const row = useQuery({
    queryKey: [entity, id],
    queryFn: () => api.get<Row>(`/${entity}/${id}`),
    enabled: !isNew,
  });
  const operation = useCardOperation(entity, row.data);
  const guided = option("use_guided_forms");
  // UX1-073/074: the stepwise card is a rollout option on top of the guided forms; without either — the card of HEAD
  const stepwiseAllowed = option("use_stepwise_ui") && guided;
  // UXW-47 (ТЗ «Рабочие столы», 31.10.2026): the grouped action panel — the full card draws it in its form, the
  // stepwise card under the step block
  const panelOn = option("use_action_panel");
  // the same query (and cache) as the card's WorkflowPanel: the page only reads which tab is the next step
  const hasWorkflow = !isNew && guided && !!meta.data?.workflow;
  const workflow = useQuery({
    queryKey: [entity, id, "workflow"],
    queryFn: () => api.get<Workflow>(`/${entity}/${id}/workflow`),
    enabled: hasWorkflow,
  });
  const nextTab = workflow.data?.recommended?.kind === "tab" ? workflow.data.recommended.key ?? null : null;
  const prefill = useMemo(() => Object.fromEntries(searchParams.entries()), [searchParams]);

  // ---- the view: the address → the saved preference → this browser → «по шагам» while the option is on ----------
  const [viewChoice, setViewChoice] = useState<CardView | null>(() => parseView(searchParams.get("view")));
  const view: CardView = !stepwiseAllowed ? "full" : (viewChoice ?? parseView(preferredView) ?? readStoredView() ?? "steps");
  // UX1-009: the deep link to a step is validated against the real steps; a wrong one opens the current stage
  const [stepChoice, setStepChoice] = useState<string | null>(() => searchParams.get("step"));
  const [extraPane, setExtraPane] = useState<string | null>(null);
  const setParam = (name: string, value: string) => {
    // a new record is keyed by its query string (App.tsx): its step lives in memory only, never in the address
    if (isNew) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(name, value);
        return next;
      },
      { replace: true },
    );
  };
  const chooseView = (next: CardView) => {
    setViewChoice(next);
    storeView(next);
    savePreferences({ card_view: next }).catch(() => undefined);
    setParam("view", next);
  };

  const title = isNew
    ? (meta.data?.create_title ?? `Создать: ${(meta.data?.label ?? "").toLowerCase()}`)
    : cardTitle(meta.data?.label ?? "", row.data?.name ?? row.data?.code ?? row.data?.number ?? row.data?.username ?? row.data?.value ?? row.data?.barcode);

  // client flow 30.10.2026: a tab that needs a right of its own («Доступ в кабинет») is shown only with that right
  const children = (meta.data?.children ?? []).filter((c) => (!c.option || option(c.option)) && (!c.permission || can(c.permission)));
  const parts = useMemo(() => formParts(meta.data, guided), [meta.data, guided]);
  const workflowPending = hasWorkflow && !workflow.data && !workflow.isError;
  const workflowFailed = hasWorkflow && workflow.isError;
  const steps = useMemo(
    () => (workflowPending ? [] : buildSteps({ workflow: hasWorkflow ? workflow.data : null, parts, children })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workflowPending, hasWorkflow, workflow.data, parts, meta.data, option],
  );
  // UX1-009 / section 6 («Workflow отсутствует/ошибка»): the full card with its own control of rights, and a retry
  const stepsView = view === "steps" && !workflowFailed;
  const stepKey = defaultStep(steps, stepChoice, workflow.data);
  const step = steps.find((s) => s.key === stepKey) ?? null;
  // after an action moved the document on, the card follows to the new current stage; the first load keeps the link
  const lastStatus = useRef<string | undefined>(undefined);
  useEffect(() => {
    const status = workflow.data?.status;
    if (lastStatus.current !== undefined && status !== undefined && status !== lastStatus.current) {
      setStepChoice(null);
      setExtraPane(null);
    }
    if (status !== undefined) lastStatus.current = status;
  }, [workflow.data?.status]);
  const goStep = (key: string) => {
    setStepChoice(key);
    setExtraPane(null);
    setParam("step", key);
  };
  const openTab = (key: string) => {
    if (stepsView) {
      const target = stepOfTab(steps, key);
      if (target) goStep(target.key);
      else setExtraPane(key);
    } else setActiveTab(key);
  };
  const stepwiseProps =
    stepsView && step
      ? {
          part: step.kind === "part" ? step.part : null,
          last: step.kind !== "part" || !neighbours(steps, step.key).next,
          onPartNeeded: (part: string) => {
            const target = stepOfPart(steps, part);
            if (target) goStep(target.key);
          },
        }
      : undefined;

  // one form element for both views: the same key and the same place in the tree, whatever surrounds it
  const mainForm = <EntityForm key={`${entity}/${id}`} entity={entity} id={id} prefill={prefill} onOpenTab={openTab} onDirtyChange={setDirty} stepwise={stepwiseProps} />;

  const panes: { key: string; label: string; help?: string | null; node: React.ReactNode }[] = [
    { key: "main", label: "Основное", help: TAB_HELP.main, node: mainForm },
    ...(!isNew
      ? children.map((c) => ({
          key: c.entity,
          label: c.label,
          help: c.help ?? TAB_HELP[c.entity],
          node: (
            <EntityTable
              key={c.entity}
              entity={c.entity}
              embedded
              baseFilter={{ field: c.fk, op: "eq", value: id }}
              hideFields={[c.fk]}
              createParams={{ [c.fk]: id }}
              toolbarExtra={c.picker ? <StockPickerButton child={c} parentId={id} onOpenTab={openTab} /> : undefined}
            />
          ),
        }))
      : []),
    ...(!isNew && can("core.attachments.read") ? [{ key: "files", label: "Файлы", help: TAB_HELP.files, node: <AttachmentsTab entity={entity} id={id} /> }] : []),
    ...(!isNew && can("core.audit.read")
      ? [
          {
            key: "history",
            label: "История",
            help: TAB_HELP.history,
            node: <EntityTable key="audit" entity="audit" embedded baseFilter={{ field: "entity_id", op: "eq", value: id }} hideFields={["entity_id", "entity"]} />,
          },
        ]
      : []),
  ];
  const stepPane = step ? (step.kind === "form" || step.kind === "part" ? "main" : step.kind === "tab" ? step.tab : null) : null;
  const visiblePane = stepsView ? (extraPane ?? stepPane) : panes.some((p) => p.key === activeTab) ? activeTab : "main";
  // a pane, once opened, stays mounted (as the tabs of HEAD do): its filters and selection survive the switches
  const [visited, setVisited] = useState<Set<string>>(() => new Set(["main"]));
  useEffect(() => {
    if (visiblePane && !visited.has(visiblePane)) setVisited((prev) => new Set(prev).add(visiblePane));
  }, [visiblePane, visited]);
  const sections = panes.filter((p) => p.key !== "main" && !steps.some((s) => s.tab === p.key)).map((p) => ({ key: p.key, label: p.label }));
  const refetch = () => {
    void row.refetch();
    if (hasWorkflow) void workflow.refetch();
    // CW31-B11: an action of the stepwise card refreshes the child tabs of this record too («Оплаты»)
    if (!isNew) void refreshChildLists(queryClient, entity, id, meta.data?.children);
  };

  if (meta.isError) return <LoadProblem error={meta.error} />;
  if (!isNew && row.isError) return <LoadProblem error={row.error} />;
  const loaded = !!meta.data && (isNew || !!row.data);
  return (
    <div style={{ maxWidth: 1100 }} className={stepsView ? "wms-stepwise" : undefined} data-card-view={stepwiseAllowed ? view : undefined}>
      <Space style={{ marginBottom: 16 }} wrap>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
          Назад
        </Button>
        <Typography.Title level={3} style={{ margin: 0 }}>
          {title}
        </Typography.Title>
        {operation && <OperationTag kind={operation}>{documentWord(operation, row.data)}</OperationTag>}
        {dirty && <Tag color="orange">не сохранено</Tag>}
        {stepwiseAllowed && (
          // UX1-017 / UX1-074: a plain choice of the view of the same record; values, filters and rights stay
          <Segmented<CardView>
            data-testid="card-view"
            value={view}
            onChange={(v) => chooseView(v)}
            options={[
              { label: "По шагам", value: "steps" },
              { label: "Полная карточка", value: "full" },
            ]}
          />
        )}
      </Space>
      {workflowFailed && view === "steps" && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          data-testid="workflow-problem"
          message="Не удалось получить этапы документа — открыта полная карточка"
          description={describeError(workflow.error)}
          action={<Button size="small" onClick={() => workflow.refetch()}>Повторить</Button>}
        />
      )}
      {/* «цвет документа»: the whole card of a receipt, a shipment, a move or a write-off is tinted in its colour */}
      <Card loading={meta.isLoading || (!isNew && row.isLoading)} className={operation ? `wms-doc-card wms-doc-card-${operation}` : undefined}>
        {loaded && stepsView ? (
          workflowPending || !step ? (
            <Alert key="strip" type="info" showIcon message="Загружаем состояние документа…" data-testid="steps-loading" style={{ marginBottom: 12 }} />
          ) : (
            <StepStrip key="strip" meta={meta.data!} row={row.data} isNew={isNew} steps={steps} stepKey={stepKey} onStep={goStep} />
          )
        ) : loaded && panes.length > 1 ? (
          <Tabs
            key="tabs"
            items={panes.map((p) => ({ key: p.key, label: tabLabel(p.label, p.help, p.key === nextTab) }))}
            activeKey={visiblePane ?? "main"}
            onChange={setActiveTab}
          />
        ) : null}
        <div key="box" className={stepsView ? "wms-step-body" : undefined}>
          {loaded && stepsView && step && (
            <StepBlock
              key="block"
              entity={entity}
              id={id}
              meta={meta.data!}
              row={row.data}
              workflow={workflow.data}
              isNew={isNew}
              steps={steps}
              step={step}
              extraPane={extraPane ? (panes.find((p) => p.key === extraPane) ?? null) : null}
              onBackToStep={() => setExtraPane(null)}
              onDone={refetch}
              panel={panelOn && !isNew && !!meta.data?.actions.length}
            />
          )}
          {loaded && stepsView && step && panelOn && !isNew && !!meta.data?.actions.length && (
            // UXW-47/49: the same grouped panel as in the full card, at one stable place under the step: an open dialog
            // survives a new status and the move to the next step; hidden (not unmounted) while a section is open
            <div key="panel" style={extraPane ? { display: "none" } : undefined} data-testid="step-panel">
              <ActionPanel
                entity={entity}
                id={id}
                meta={meta.data!}
                row={row.data}
                workflow={workflow.data ?? null}
                view="steps"
                viewingLive={step.state === "current" || step.state === "waiting"}
                onDone={refetch}
                onOpenTab={openTab}
              />
            </div>
          )}
          <div key="panes" className="wms-panes">
            {panes.map((p) => (
              <div key={p.key} data-pane={p.key} style={p.key === visiblePane ? undefined : { display: "none" }}>
                {(p.key === "main" || p.key === visiblePane || visited.has(p.key)) && p.node}
              </div>
            ))}
          </div>
        </div>
        {loaded && stepsView && step && (
          <StepFooter
            key="footer"
            entity={entity}
            id={id}
            meta={meta.data!}
            row={row.data}
            workflow={workflow.data}
            isNew={isNew}
            steps={steps}
            step={step}
            onStep={goStep}
            onFullCard={() => chooseView("full")}
            sections={sections}
            extraPane={extraPane}
            onSection={(key) => setExtraPane((current) => (current === key ? null : key))}
            onDone={refetch}
          />
        )}
      </Card>
    </div>
  );
}
